/**
 * Safely parse a year-like input (number or string) into a numeric year.
 *
 * Rules:
 *  - Accepts finite numbers (e.g., 2024).
 *  - Accepts numeric strings (e.g., "2024", " 2024 ").
 *  - Rejects empty strings, whitespace-only strings, non-numeric strings, and non-finite numbers.
 *
 * @param {number|string|null|undefined} value
 *   The value to parse into a year.
 *
 * @returns {number|null}
 *   A valid year number, or null if the input cannot be parsed.
 */
function parseYear(value) {
  if (value == null) return null;

  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null; // empty/whitespace → invalid

    const num = Number(trimmed);
    return Number.isFinite(num) ? num : null;
  }

  return null;
}

/**
 * Convert a starting fiscal year (e.g., 2005) into the formatted range "2005-2006".
 *
 * Intended for converting integer fiscal years used throughout budget data
 * into user-facing text.
 *
 * @param {number|string} startYear
 *   A year value representing the *first* year of the fiscal period.
 *
 * @returns {string}
 *   The formatted range "YYYY-YYYY", or an empty string if the input is invalid.
 */
export function formatFiscalYearFromStart(startYear) {
  const year = parseYear(startYear);
  if (year == null) return "";
  return `${year}-${year + 1}`;
}

/**
 * Convert a range key like "2009_2010" into "2009-2010".
 *
 * This is used for objects keyed by ranges (e.g., programBudgetByYear).
 * The function:
 *  - Accepts only strings in the exact "start_end" format.
 *  - Validates both start and end years numerically.
 *  - Returns the original string if validation fails, preserving existing keys.
 *
 * @param {string} rangeKey
 *   A string representing a fiscal range in the format "start_end".
 *
 * @returns {string}
 *   The formatted range "start-end", or the original string if invalid.
 */
export function formatFiscalYearFromRangeKey(rangeKey) {
  if (typeof rangeKey !== "string") return "";

  const parts = rangeKey.split("_");
  if (parts.length !== 2) {
    return rangeKey; // not a valid pattern, return unchanged
  }

  const [startStr, endStr] = parts;
  const start = parseYear(startStr);
  const end = parseYear(endStr);

  if (start == null || end == null) return rangeKey;
  return `${start}-${end}`;
}
